import React, { useState } from 'react';
import { Typography, Grid, Skeleton } from '@mui/material';
import openIcon from 'assets/openIcon.svg';
import noResumeIcon from 'assets/noResume.svg';
import ResumeDialog from '../CandidateResume/ResumeDialog';
import {
  decodeBase64URL,
  formatResumeFilename,
} from 'modules/hiring/utilities';
import useCandidateResume from 'modules/hiring/hooks/useCandidateResume';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { colors } from 'styles/colors';
import { Stack } from '@mui/system';
import useCandidateResumeDownload from 'modules/hiring/hooks/useCandidateResumeDownload';

export const resumeFileTypes = {
  JVBERi0: 'data:application/pdf;base64',
  UEsDBBQ:
    'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64',
  UEsDBBQA:
    'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64',
};
export interface ICandidateModalResumeProps {
  candidateId: number | undefined;
  candidateName: string;
  fromFullProfile?: boolean;
  showLoader?: boolean;
}
const CandidateModalResume = ({
  candidateId,
  candidateName,
  fromFullProfile,
}: ICandidateModalResumeProps) => {
  const [open, setOpen] = useState(false);
  const { resumeDetailsData: resume, loading: isLoading } =
    useCandidateResume(candidateId);
  const resumeFileName = resume?.candidateResumeList?.[0]?.fileName;
  const resumeBlobUrl = resume?.candidateResumeList?.[0]?.blobURL;
  const fileType = resumeFileName?.toLowerCase().endsWith('.pdf')
    ? 'pdf'
    : 'docx';
  const { resumeDetailsDownload: resumeDownload, loading: resumeLoading } =
    useCandidateResumeDownload(resumeFileName ?? '', open);
  const downloadURL =
    decodeBase64URL(resumeDownload?.resumeDetails, resumeFileName) ??
    resumeBlobUrl ??
    undefined;
  const showResume = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Grid>
        {isLoading ? (
          <Skeleton
            sx={{
              bgcolor: (theme) => theme.palette.neutral?.main,
              marginBottom: fromFullProfile ? '2rem' : 0,
            }}
            variant='rectangular'
            height={fromFullProfile ? 80 : 24}
            width={'100%'}
            animation='wave'
            data-testid='candidate-resume-loader'
          />
        ) : (
          <>
            {resume?.candidateResumeList?.[0]?.resumeUrl ? (
              <Typography
                id='candidate-name-resume'
                className='kfic2-bodyTextLink'
                data-testid='candidate-name-resume'
                onClick={showResume}
                title={formatResumeFilename(resumeFileName ?? '')}
                style={{
                  marginBottom: fromFullProfile ? '1.8rem' : 0,
                  marginRight: '1rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {formatResumeFilename(resumeFileName ?? '')}
                <img
                  src={openIcon}
                  title='Resume'
                  alt='resume icon'
                  data-testid='thumbnail'
                  style={{ marginTop: 3, position: 'relative', paddingLeft: 8 }}
                />
              </Typography>
            ) : (
              <>
                {fromFullProfile ? (
                  <Stack
                    flexDirection='row'
                    gap={2}
                    mb={3}
                    id='full-profile-candidate-resume-container'
                    data-testid='full-profile-candidate-resume-container'
                  >
                    <img
                      src={noResumeIcon}
                      title='No Resume'
                      alt='no resume icon'
                      style={{
                        width: '7.453rem',
                        height: '3.981rem',
                      }}
                    />
                    <Stack spacing={1} textAlign='center'>
                      <TypographyWithI18N
                        className='kfic2-bodyTextBold'
                        id='full-profile-candidate-no-resume'
                        data-testid='full-profile-candidate-no-resume'
                        i18nKey='noResumeAvailable'
                      />
                      <TypographyWithI18N
                        className='kfic2-bodyText'
                        id='full-profile-candidate-not-all'
                        data-testid='full-profile-candidate-not-all'
                        i18nKey='notAllRolesRequireAResumeToApply'
                        textSuffix='.'
                      />
                    </Stack>
                  </Stack>
                ) : (
                  <TypographyWithI18N
                    className='kfic2-bodyTextBold'
                    id='candidate-no-resume'
                    data-testid='candidate-no-resume'
                    i18nKey='noResumeAvailable'
                    style={{
                      display: 'flex',
                      color: `${colors.tertiary.darkGray}`,
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </Grid>
      <ResumeDialog
        open={open}
        resumeFileName={resumeFileName ?? ''}
        candidateName={candidateName ?? ''}
        onClose={handleClose}
        fileType={fileType}
        docLink={downloadURL}
        showLoader={open && resumeLoading}
        resume={resumeDownload?.resumeDetails}
      />
    </>
  );
};

export default CandidateModalResume;
